<template>
  <div>
    <WizardModal
      modal-id="RtspServerWizard"
      :is-active.sync="isWizardActive"
      :is-add="isAdd"
      :wizard-title="wizardTitle"
      :steps="wizardSteps"
      :step-icons="wizardStepIcons"
      :finishAtStep="finishStep"
      :current-step="currentStep"
      :loading="loadingStep"
      :show-previous-button="!isAdd"
      :show-save-button="!isAdd && currentStep < finishStep"
      :show-next-button="!isAdd && currentStep < finishStep"
      :show-finish-button="isAdd || currentStep === finishStep"
      :activate-btn-save="!isAdd"
      :has-config="
        !isAdd &&
        itemData.config !== null &&
        itemData.config !== undefined &&
        Object.keys(itemData.config || {}).length > 0
      "
      @close-wizard="closeWizard"
      @prev-step="prevStep"
      @next-step="nextStep"
      @save-data="handleSave"
      @load-data="loadData"
      @reset-wizard="resetData"
      @on-submit="onSubmit"
    >
      <template #step-0="{ currentStep, loading }">
        <GeneralStep
          ref="generalStep"
          :prop-item-data="itemData"
          :tenant-options="tenantOptions"
          @update:itemData="itemData = { ...itemData, ...$event }"
        />
      </template>
      <template #step-1="{ currentStep, loading }">
        <LogsStep
          ref="logsStep"
          :prop-item-data="itemData"
          @update:itemData="updateItemDataFromStep"
        />
      </template>
      <template #step-2="{ currentStep, loading }">
        <ApiStep
          ref="apiStep"
          :prop-item-data="itemData"
          @update:itemData="updateItemDataFromStep"
        />
      </template>
      <template #step-3="{ currentStep, loading }">
        <MetricsStep
          ref="metricsStep"
          :prop-item-data="itemData"
          @update:itemData="updateItemDataFromStep"
        />
      </template>
      <template #step-4="{ currentStep, loading }">
        <StreamingStep
          ref="streamingStep"
          :prop-item-data="itemData"
          @update:itemData="updateItemDataFromStep"
        />
      </template>
    </WizardModal>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch, nextTick } from "@vue/composition-api";
import { useToast } from "vue-toastification/composition";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import axios from "@axios";
import store from "@/store";
import WizardModal from "@/components/WizardModal/Index.vue";

import GeneralStep from "./GeneralStep.vue";
import LogsStep from "./LogsStep.vue";
import ApiStep from "./ApiStep.vue";
import MetricsStep from "./MetricsStep.vue";
import StreamingStep from "./StreamingStep.vue";

export default {
  name: "RtspServerWizard",
  components: {
    WizardModal,
    GeneralStep,
    LogsStep,
    ApiStep,
    MetricsStep,
    StreamingStep,
  },
  props: {
    isActive: {
      type: Boolean,
      required: true,
    },
    itemEdit: {
      type: Object,
      required: false,
    },
    isAdd: {
      type: Boolean,
      default: false,
    },
    tenantOptions: {
      type: Array,
      required: true,
    },
  },
  setup(props, { emit }) {
    const { t } = useI18nUtils();
    const toast = useToast();
    const isWizardActive = ref(props.isActive);
    const wizardTitle = "RTSP Server Wizard";
    const currentStep = ref(0);
    const finishStep = computed(() => {
      return props.isAdd ? 0 : stepRefs.value.length - 1;
    });
    const loadingStep = ref(false);
    const debugMode = ref(false);

    const generalStep = ref(null);
    const logsStep = ref(null);
    const apiStep = ref(null);
    const metricsStep = ref(null);
    const streamingStep = ref(null);
    const localItemData = ref(null);

    const stepRefs = ref([generalStep, logsStep, apiStep, metricsStep, streamingStep]);

    const wizardSteps = ["General", "Logs", "API", "Metrics", "Streaming"];

    const wizardStepIcons = [
      "SettingsIcon",
      "FileTextIcon",
      "CloudIcon",
      "PieChartIcon",
      "TvIcon",
    ];

    const validateCurrentStep = async () => {
      if (debugMode.value) return true;

      await nextTick();
      const currentRef = stepRefs.value[currentStep.value]?.value;
      if (currentRef && typeof currentRef.validateStep === "function") {
        const validation = await currentRef.validateStep();
        return validation;
      }
      return true;
    };

    const blackData = {
      id: null,
      name: "",
      api_host: "",
      rtsp_host: "",
      playback_host: "",
      hls_host: "",
      webrtc_host: "",
      id_tenant: 0,
      tenant: {
        id: 0,
        name: "",
      },
      online: 0,
      created_at: "",
      updated_at: "",
      config: null,
    };

    const itemData = ref(JSON.parse(JSON.stringify(blackData)));

    const updateItemData = (data) => {
      if (!data || (Object.keys(data).length === 1 && data.config)) {
        console.warn("Datos incompletos para actualizar itemData:", data);
        return;
      }

      const currentData = JSON.parse(JSON.stringify(itemData.value));
      const newData = JSON.parse(JSON.stringify(data));

      const mergedData = {
        ...currentData,
        ...newData,
      };

      if (
        !props.isAdd &&
        mergedData.id &&
        (!newData.config || Object.keys(newData.config || {}).length === 0)
      ) {
        mergedData.config = currentData.config || {};
      }

      itemData.value = mergedData;
    };

    const updateItemDataFromStep = (data) => {
      if (data && typeof data === "object") {
        const updatedData = { ...itemData.value };

        Object.keys(data).forEach((key) => {
          if (key !== "config") {
            updatedData[key] = data[key];
          }
        });

        if (data.config && typeof data.config === "object") {
          if (!updatedData.config) {
            updatedData.config = {};
          }
          updatedData.config = { ...updatedData.config, ...data.config };
        }

        itemData.value = updatedData;
      }
    };

    const loadGlobalConfig = async () => {
      try {
        loadingStep.value = true;

        if (!itemData.value.id || itemData.value.id === 0) {
          return false;
        }

        const currentItemData = JSON.parse(JSON.stringify(itemData.value));

        // ===== INICIO DE CÓDIGO SIMULADO =====

        // const response = {
        //   data: {
        //     success: true,
        //     data: JSON.parse(JSON.stringify(configData)),
        //   },
        // };
        // ===== FIN DE CÓDIGO SIMULADO =====

        const response = await axios.get(
          `v1/proxy_rtsp_servers/get_global_config?id=${itemData.value.id}`
        );

        if (response.data && response.data.success) {
          currentItemData.config = response.data.data;
          itemData.value = currentItemData;
          return true;
        } else {
          currentItemData.config = {};
          itemData.value = currentItemData;
          return false;
        }
      } catch (error) {
        console.error("Error al cargar la configuración:", error);
        const currentItemData = JSON.parse(JSON.stringify(itemData.value));
        currentItemData.config = {};
        itemData.value = currentItemData;
        return false;
      } finally {
        loadingStep.value = false;
      }
    };

    const loadData = async () => {
      try {
        if (props.itemEdit && props.itemEdit.id) {
          itemData.value = JSON.parse(JSON.stringify(props.itemEdit));
          if (
            !props.isAdd &&
            (!itemData.value.config ||
              itemData.value.config === null ||
              Object.keys(itemData.value.config || {}).length === 0)
          ) {
            const success = await loadGlobalConfig();
            if (!success) {
              console.warn("No se pudo cargar la configuración global");
              itemData.value.config = {};
            }
          }
        } else {
          itemData.value = JSON.parse(JSON.stringify(blackData));
        }
        emit("load-data");
      } catch (error) {
        console.error("Error cargando datos:", error);
        toast.error("load_error");
        if (props.isAdd) {
          itemData.value.config = null;
        } else {
          itemData.value.config = {};
        }
      }
    };
    const prevStep = () => {
      if (currentStep.value > 0) {
        currentStep.value--;
      }
      emit("prev-step");
    };

    const nextStep = async () => {
      try {
        if (props.isAdd && currentStep.value >= 0) {
          return;
        }

        if (!debugMode.value) {
          const isValid = await validateCurrentStep();
          if (!isValid) {
            return;
          }
        }

        loadingStep.value = true;

        if (currentStep.value === 0 && props.isAdd && !itemData.value.id) {
          try {
            const dataToCreate = JSON.parse(JSON.stringify(itemData.value));
            dataToCreate.config = null;

            const response = await store.dispatch("proxy_rtsp_server/add", dataToCreate);

            if (!response.success) {
              toast.error(response.message || "save_error");
              return;
            }

            let serverData;
            if (Array.isArray(response.data)) {
              serverData = response.data[0] || null;
            } else {
              serverData = response.data || null;
            }

            updateItemData(serverData);
            emit("update:itemEdit", serverData);

            closeWizard();
          } catch (error) {
            console.error("Error al crear el servidor:", error);
          }
        } else {
          currentStep.value++;
        }
      } catch (error) {
        toast.error("save_error");
      } finally {
        loadingStep.value = false;
      }
    };

    const saveGeneralStepOnly = async () => {
      try {
        if (!itemData.value || Array.isArray(itemData.value) || !itemData.value.id) {
          return false;
        }

        const generalData = {
          id: itemData.value.id,
          name: itemData.value.name,
          api_host: itemData.value.api_host,
          rtsp_host: itemData.value.rtsp_host,
          playback_host: itemData.value.playback_host,
          hls_host: itemData.value.hls_host,
          webrtc_host: itemData.value.webrtc_host,
          id_tenant: itemData.value.id_tenant,
          tenant: itemData.value.tenant,
          online: itemData.value.online,
          created_at: itemData.value.created_at,
          updated_at: itemData.value.updated_at,
          config: itemData.value.config || null,
        };

        const req = {
          id: generalData.id,
          params: generalData,
        };

        const response = await store.dispatch("proxy_rtsp_server/edit", req);
        if (!response.success) {
          toast.error(response.message || "save_error");
          return false;
        }

        if (response.data) {
          let updatedData;
          if (Array.isArray(response.data)) {
            updatedData = response.data[0] || {};
          } else {
            updatedData = response.data;
          }

          const mergedData = {
            ...itemData.value,
            ...updatedData,
            config: itemData.value.config || updatedData.config || {},
          };

          itemData.value = mergedData;
        }

        return true;
      } catch (error) {
        console.error("Error saving general step:", error);
        toast.error("save_error");
        return false;
      }
    };

    const saveData = async (isFinalStep) => {
      try {
        const isValid = await validateCurrentStep();
        if (!isValid) {
          return false;
        }
        if (!itemData.value || Array.isArray(itemData.value)) {
          return false;
        }
        if (itemData.value && itemData.value.id) {
          const currentCompleteData = JSON.parse(JSON.stringify(itemData.value));

          const req = {
            id: currentCompleteData.id,
            params: currentCompleteData,
          };

          const response = await store.dispatch("proxy_rtsp_server/edit", req);
          if (!response.success) {
            toast.error(response.message || "save_error");
            return false;
          }

          if (response.data) {
            let updatedData;

            if (Array.isArray(response.data)) {
              updatedData = response.data[0] || {};
            } else {
              updatedData = response.data;
            }
            const mergedData = {
              ...currentCompleteData,
              ...updatedData,
              config: currentCompleteData.config || updatedData.config || {},
            };

            itemData.value = mergedData;
          }
          toast.success("Datos guardados correctamente");
        }

        if (isFinalStep) {
          emit("save-data", itemData.value);
        } else {
          emit("update:itemData", itemData.value);
        }
        return true;
      } catch (error) {
        toast.error("Error al guardar: " + error.message);
        return false;
      }
    };

    const goToStep = async (index) => {
      if (debugMode.value) {
        currentStep.value = index;
        return;
      }
      if (index > currentStep.value) {
        const isValid = await validateCurrentStep();
        if (!isValid) {
          toast.error("validation_error");
          return;
        }
        if (currentStep.value === 0 && !props.isAdd) {
          const saveSuccessful = await saveGeneralStepOnly();
          if (!saveSuccessful) {
            return;
          }
        }
      }

      currentStep.value = index;
    };

    const handleSave = async () => {
      try {
        loadingStep.value = true;
        const isSuccessful = await saveData(true);
        if (isSuccessful) {
          toast.success("Cambios guardados correctamente");
          emit("refetch-data"); // Para actualizar la tabla principal
        } else {
          toast.error("Error al guardar los cambios");
        }
      } catch (error) {
        console.error("Error en handleSave:", error);
        toast.error("Error al guardar los cambios");
      } finally {
        loadingStep.value = false;
      }
    };

    const resetData = () => {
      emit("reset-wizard");
    };
    const onSubmit = async () => {
      try {
        loadingStep.value = true;
        const dataToSubmit = JSON.parse(JSON.stringify(itemData.value));

        if (dataToSubmit.id && !dataToSubmit.config) {
          dataToSubmit.config = {};
          toast.info("Se guardará el servidor con una configuración básica");
        }
        if (!dataToSubmit.id) {
          dataToSubmit.config = null;
        }

        let response;
        if (dataToSubmit.id) {
          const req = {
            id: dataToSubmit.id,
            params: dataToSubmit,
          };
          response = await store.dispatch("proxy_rtsp_server/edit", req);
        } else {
          response = await store.dispatch("proxy_rtsp_server/add", dataToSubmit);
        }

        if (response.success) {
          toast.success(response.message || "Save success");
          emit("refetch-data");
          closeWizard();
        } else {
          toast.error(response.message || "Save error");
        }
      } catch (error) {
        toast.error("Error al guardar los datos");
      } finally {
        loadingStep.value = false;
      }
    };
    watch(
      () => props.isActive,
      (newValue) => {
        isWizardActive.value = newValue;
      }
    );

    watch(
      () => props.itemEdit,
      (newVal) => {
        localItemData.value = JSON.parse(JSON.stringify(newVal || blackData));
      },
      { immediate: true, deep: true }
    );

    onMounted(() => {
      nextTick(() => {
        isWizardActive.value = props.isActive;
      });
    });

    const closeWizard = () => {
      emit("update:isActive", false);
      currentStep.value = 0;
      if (props.isAdd) {
        emit("update:itemEdit", {});
      }
      emit("close-wizard");
    };

    return {
      isWizardActive,
      wizardTitle,
      closeWizard,
      prevStep,
      nextStep,
      handleSave,
      loadData,
      resetData,
      onSubmit,
      wizardSteps,
      wizardStepIcons,
      itemData,
      generalStep,
      logsStep,
      apiStep,
      metricsStep,
      streamingStep,
      t,
      updateItemData,
      validateCurrentStep,
      stepRefs,
      currentStep,
      loadingStep,
      finishStep,
      goToStep,
      updateItemDataFromStep,
      saveGeneralStepOnly,
    };
  },
};
</script>
